import React, {useEffect, useMemo, useRef} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';

import {useForm} from 'react-hook-form';

import {useTranslation} from 'react-i18next';

import {I18N_NS} from '../../../core/constants/names';
import {
    $allowedActionsForInvoice,
    SubmitTaxesFormPayload,
    submitTaxesFormRequested,
} from '../../../models/common';
import {
    $taxesAndTotal,
    updateTaxesAndTotal,
    updateTaxesAndTotalRoundAndPrepayd,
} from '../../../models/create';
import {RoundAndPrepayd, TaxesAndTotal} from '../../../types/invoices';

import {ControlSection} from '../../ControlSection';

import {formData} from '../../FormsData/TaxesTotal';

import {isStandartCategory, isZeroCategory} from '../../helpers/consts';
import {UniController} from '../../universalController';

import {ChargesList} from './components/ChargesTable';
import {CalcDisplay, Grid, Headline, Hr, Label, LableContainer, Root} from './styled';
import {createIsFieldRequired, createValidationContext, generalInfoSchema} from './validation';

export const TaxesAndTotalTab = () => {
    const {t} = useTranslation(I18N_NS);
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);
    const taxesAndTotal = useStore($taxesAndTotal);
    const submitRef = useRef<(payload?: SubmitTaxesFormPayload) => void>(() => {});
    const formContext = useMemo(() => createValidationContext(taxesAndTotal.data), [
        taxesAndTotal.data,
    ]);

    const {
        control,
        handleSubmit,
        setValue,
        formState: {errors},
    } = useForm({
        defaultValues: {...taxesAndTotal.data},
        resolver: yupResolver(generalInfoSchema),
        context: formContext,
    });

    const isFieldRequired = useMemo(() => createIsFieldRequired(formContext), [formContext]);

    function submitWithPayload(data: TaxesAndTotal, payload?: SubmitTaxesFormPayload) {
        data.calculationsByTaxCategory = data.calculationsByTaxCategory.map((i, index) => ({
            ...taxesAndTotal.data.calculationsByTaxCategory[index],
            ...i,
        }));
        updateTaxesAndTotal({
            data,
            allowToSend: true,
        });
    }

    submitRef.current = (payload) => {
        handleSubmit((data) => submitWithPayload(data, payload))();
    };

    useEffect(
        () =>
            submitTaxesFormRequested.watch((payload) => {
                submitRef.current?.(payload);
            }),
        [],
    );

    const GEN_INFO = 'pages.documents.invoices.create';

    const formDataList = formData(t, allowedActionsForInvoice.edit, isFieldRequired);

    function handleValueChange(val: {name: string; value: unknown}) {
        const valueToProccess = ['payableRoundingAmount', 'prepaidAmount'];
        if (!valueToProccess.includes(val.name)) {
            return;
        }
        updateTaxesAndTotalRoundAndPrepayd(val as RoundAndPrepayd);
    }

    // useEffect(() => {
    //     const q = Number(taxesAndTotal.data.payableRoundingAmount) || 0;
    //     const e = Number(taxesAndTotal.data.prepaidAmount) || 0;

    //     setValue('payableAmount', Number(taxesAndTotal.data.payableAmount) - e + q);
    // }, [taxesAndTotal.data.payableRoundingAmount, taxesAndTotal.data.prepaidAmount]);

    useEffect(() => {
        setValue('payableAmountDisplay', taxesAndTotal.data.payableAmountDisplay);
    }, [taxesAndTotal.data.payableAmountDisplay]);

    useEffect(() => {
        setValue('chargeTotalAmountDisplay', taxesAndTotal.data.chargeTotalAmountDisplay);
        setValue('allowanceTotalAmountDisplay', taxesAndTotal.data.allowanceTotalAmountDisplay);
        setValue('taxExclusiveAmountDisplay', taxesAndTotal.data.taxExclusiveAmountDisplay);
        setValue('taxInclusiveAmountDisplay', taxesAndTotal.data.taxInclusiveAmountDisplay);
    }, [
        taxesAndTotal.data.chargeTotalAmountDisplay,
        taxesAndTotal.data.allowanceTotalAmountDisplay,
    ]);

    return (
        <Root>
            <Headline>{t(`${GEN_INFO}.taxesTotal.headlines.taxes`)}</Headline>
            {taxesAndTotal.data.calculationsByTaxCategory.map((i, index) => {
                const isTaxExemptionReasonDisabled =
                    isZeroCategory(i.taxCategory as string) ||
                    isStandartCategory(i.taxCategory as string);
                return (
                    <Grid key={`${i.taxCategory} - ${index}`} columns={'1fr 1fr 1fr 1fr'}>
                        <LableContainer>
                            <Label>{t(`${GEN_INFO}.taxesTotal.taxCategory.label`)}</Label>
                            <CalcDisplay>{i.taxCategoryDisplay}</CalcDisplay>
                        </LableContainer>
                        <LableContainer>
                            <Label>{t(`${GEN_INFO}.taxesTotal.rate.label`)}</Label>
                            <CalcDisplay>{i.rate}%</CalcDisplay>
                        </LableContainer>
                        <LableContainer>
                            <Label>{t(`${GEN_INFO}.taxesTotal.taxableAmount.label`)}</Label>
                            <CalcDisplay>{i.taxableAmountDisplay}</CalcDisplay>
                        </LableContainer>
                        <LableContainer>
                            <Label>{t(`${GEN_INFO}.taxesTotal.taxAmount.label`)}</Label>
                            <CalcDisplay>{i.taxAmountDisplay}</CalcDisplay>
                        </LableContainer>
                        <div style={{gridColumn: '3/-1'}}>
                            <UniController
                                index={index}
                                item={{
                                    defaultVal: i.taxExemptionReason ?? '',
                                    type: 'input',
                                    label: t(`${GEN_INFO}.taxesTotal.upd.taxExemptionReason`),
                                    placeholder: isTaxExemptionReasonDisabled
                                        ? '—'
                                        : t(`${GEN_INFO}.taxesTotal.upd.describeTheReason`),
                                    name: `calculationsByTaxCategory[${index}].taxExemptionReason`,
                                    disabled: !allowedActionsForInvoice.edit
                                        ? true
                                        : isTaxExemptionReasonDisabled,
                                    required: !isTaxExemptionReasonDisabled,
                                }}
                                errors={errors}
                                control={control}
                                setValue={setValue}
                            />
                        </div>
                        <Hr />
                    </Grid>
                );
            })}
            <Grid columns={'1fr 1fr 1fr 1fr'}>
                <LableContainer style={{gridColumn: '3/-1'}}>
                    <Label>{t(`${GEN_INFO}.taxesTotal.taxTotal.label`)}</Label>
                    <CalcDisplay>{taxesAndTotal.data.taxTotal}</CalcDisplay>
                </LableContainer>
            </Grid>
            <Headline>{t(`${GEN_INFO}.taxesTotal.headlines.withholding`)}</Headline>
            <Grid columns={'1fr'}>
                <UniController
                    item={{
                        type: 'input',
                        label: t(`${GEN_INFO}.taxesTotal.upd.withholdingTaxLabel`),
                        placeholder: t(`${GEN_INFO}.taxesTotal.upd.withholdingTaxPlaceholder`),
                        name: 'withholdingTax',
                        disabled: !allowedActionsForInvoice.edit,
                    }}
                    errors={errors}
                    control={control}
                    setValue={setValue}
                />
            </Grid>
            {(allowedActionsForInvoice.edit || !!taxesAndTotal.data.allowanceCharge?.length) && (
                <>
                    <div>{t(`${GEN_INFO}.taxesTotal.subHeadlines.charges`)}</div>
                    <ChargesList />
                </>
            )}
            <Headline>{t(`${GEN_INFO}.taxesTotal.headlines.totals`)}</Headline>
            <Grid columns={'1fr 1fr 1fr'}>
                {formDataList.charges.map((item, index) => {
                    if (!item.name) {
                        return <div key={`${index}_${item.name}}`} />;
                    }

                    return (
                        <UniController
                            key={`${index}_${item.name}}`}
                            item={item}
                            index={index}
                            errors={errors}
                            control={control}
                            setValue={setValue}
                            cb={(val) => {
                                handleValueChange(val);
                            }}
                        />
                    );
                })}
            </Grid>
            <ControlSection onSubmit={handleSubmit((data) => submitWithPayload(data))} />
            {/* <button type="submit" onClick={handleSubmit(onSubmit)}>
                send
            </button> */}
        </Root>
    );
};
